import {
  Card, CardHeader, Typography, Stack,
} from '@mui/material';
// utils
import { useMemo } from 'react';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../hooks/useDashboard';

type ProgressItemProps = {
  progress: any;
};

const BorderLinearProgress = styled(LinearProgress)(({ theme, color }) => ({
  height: 6,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: color, // Fallback to default if no color is passed
    ...theme.applyStyles('dark', {
      backgroundColor: color, // Apply passed color in dark mode
    }),
  },
}));

function ProgressItem({ progress }: ProgressItemProps) {
  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {progress.title}
        </Typography>
        <Typography variant="subtitle2">{progress.value}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          &nbsp;(
          {progress.formattedValue || 0}
          %
          )
        </Typography>
      </Stack>

      <BorderLinearProgress
        variant="determinate"
        value={progress.formattedValue || 0}
        color={progress.color} // Pass the color here
      />
    </Stack>
  );
}

const DashboardTimesheets = () => {
  const { getTimesheetsItems } = useDashboard();
  const { t } = useTranslation('dashboard');

  const timesheetsItems = useMemo(() => {
    const totalValue = getTimesheetsItems.reduce((sum, item) => {
      // Remove "$" and "," then parse as float
      const numericValue = parseFloat(item.value.toString().replace(/[$,]/g, ''));
      return sum + numericValue;
    }, 0);

    return getTimesheetsItems.map((item) => {
      const numericValue = parseFloat(item.value.toString().replace(/[$,]/g, ''));
      const percentageValue = (numericValue / totalValue) * 100;

      return {
        ...item,
        formattedValue: parseFloat(percentageValue.toFixed(2)),
      };
    });
  }, [getTimesheetsItems]);

  return (
    <Card>
      <CardHeader title={t('timesheetsOverview')} />

      <Stack spacing={4} sx={{ p: 3 }}>
        {timesheetsItems.map((timesheet) => (
          <ProgressItem key={timesheet.id} progress={timesheet} />
        ))}
      </Stack>
    </Card>
  );
};

export {
  DashboardTimesheets,
};
